import React, { useCallback } from 'react';

import { FlexRow } from 'ui-components/components/flex';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import style from './style.module.less';
import { getUpsellModalContent } from '../../../../../components/upsell-modal/helper';
import { useSellerFormattedDate } from '../../../../../hooks/use-datetime-formats';
import { AddOnActionMenu } from '../../../components/add-on-tile/add-on-action-menu/add-on-action-menu';
import messages from '../../../components/add-on-tile/messages';
import { sources as MarketplaceConnectionSources } from '../../../stores/tracking-events';
import '../../../../../prop-types/add-on.types';
import { getLineItemPriority, LineItemPriorities } from '../../components/manage-subscription-drawer/helpers';

/**
 * @typedef {import('../../../../../prop-types/add-on.types').AddOnPropType} AddOn
 */

/**
 * @param {object} props
 * @param {AddOn} props.addOn
 * @param {(code: string) => void} props.onDeactivateAddOn
 * @param {(code: string) => void} props.onManageAddOnSettings
 * @param {(code: string, source: string) => void} props.onReactivateAddOn
 * @returns {JSX.Element}
 */
export const AddOnRow = ({ addOn, onDeactivateAddOn, onManageAddOnSettings, onReactivateAddOn }) => {
  const content = getUpsellModalContent(addOn.code);
  const { isTrial } = addOn;
  const deactivationDate = useSellerFormattedDate(addOn?.deactivationDateTimeUTC);
  const onClickDeactivateAddon = useCallback(() => onDeactivateAddOn(addOn.code), [addOn.code, onDeactivateAddOn]);
  const onClickManageAddOnSettings = useCallback(() => onManageAddOnSettings(addOn.code), [addOn.code, onManageAddOnSettings]);
  const onClickReactivateAddon = useCallback(
    () => onReactivateAddOn(addOn.code, MarketplaceConnectionSources.PAYMENT_AND_SUBSCRIPTIONS_PAGE),
    [addOn.code, onReactivateAddOn],
  );

  // A marketplace addOn tile populates data based on what feature access or addOn the seller has, sometimes mixed states should be rendered.
  // e.g. a seller has a trial addon but then converts to HV and thus 2 active line items
  const lineItemPriority = getLineItemPriority(addOn);
  const shouldShowAddOnSpecificData =
    lineItemPriority !== LineItemPriorities.AlphaUser && lineItemPriority !== LineItemPriorities.PlanIncluded;

  return (
    <div key={addOn.code} className={style['add-ons-row']}>
      <FlexRow className={style['add-ons-name-row']}>
        <Text
          className={style['add-ons-name']}
          element='div' // fixes a proptype warning where you can't put a div inside a html <p> tag
          textType={TextType.Paragraph}
          value={content?.name || addOn.code}
        />
        {isTrial && !deactivationDate ? <Text textType={TextType.Caption} value={messages.trial} /> : null}
        {shouldShowAddOnSpecificData && deactivationDate ? (
          <Text
            formattedMessageValues={{
              deactivationDt: deactivationDate,
            }}
            textType={TextType.Caption}
            value={messages.deactivationDate}
          />
        ) : null}
      </FlexRow>
      <AddOnActionMenu
        addOn={addOn}
        onClickDeactivate={onClickDeactivateAddon}
        onClickManageSettings={onClickManageAddOnSettings}
        onClickReactivate={onClickReactivateAddon}
        shouldShowManageSettingsOption
      />
    </div>
  );
};
